import React, {useEffect, useRef, useState} from 'react';
import {Alert, Box, Button, Card, CardContent, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, TextField, Typography,} from '@mui/material';
import {CheckCircle as CheckIcon, CloudUpload as UploadIcon, Delete as DeleteIcon, Draw as SignatureIcon, Image as ImageIcon,} from '@mui/icons-material';
import {TaskImage} from '../../../../../services/openapi/models/TaskImage';

interface RapportCompleteModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (rapportData: {
        rapportText: string;
        selectedTaskImages: TaskImage[];
        uploadedImages: File[];
        signature: string | null;
        anfahrt_abfahrt_mit_kfz: boolean;
        material: string;
        arbeitszeit: number;
        anzahl_monteure: number;
    }) => void;
    taskTitle?: string;
    initialRapportText?: string;
    taskImages?: TaskImage[];
}

export default function RapportCompleteModal({
                                                 open,
                                                 onClose,
                                                 onConfirm,
                                                 taskTitle,
                                                 initialRapportText = '',
                                                 taskImages = [],
                                             }: RapportCompleteModalProps) {
    const [rapportText, setRapportText] = useState('');
    const [selectedTaskImages, setSelectedTaskImages] = useState<TaskImage[]>([]);
    const [uploadedImages, setUploadedImages] = useState<File[]>([]);
    const [signature, setSignature] = useState<string | null>(null);
    const [signatureRequired, setSignatureRequired] = useState<boolean>(false);
    const [isDrawing, setIsDrawing] = useState(false);
    const [anfahrtAbfahrtMitKfz, setAnfahrtAbfahrtMitKfz] = useState<boolean>(false);
    const [material, setMaterial] = useState<string>('');
    const [arbeitszeit, setArbeitszeit] = useState<number>(0);
    const [anzahlMonteure, setAnzahlMonteure] = useState<number>(1);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (open) {
            setRapportText(initialRapportText);
            setSelectedTaskImages([]);
            setUploadedImages([]);
            setSignature(null);
            setSignatureRequired(false);
            setAnfahrtAbfahrtMitKfz(false);
            setMaterial('');
            setArbeitszeit(0);
            setAnzahlMonteure(1);
            clearSignature();
        }
    }, [open, initialRapportText]);

    const handleTaskImageToggle = (image: TaskImage) => {
        setSelectedTaskImages(prev => {
            const isSelected = prev.some(img => img.id === image.id);
            if (isSelected) {
                return prev.filter(img => img.id !== image.id);
            } else {
                return [...prev, image];
            }
        });
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);

        const imageFiles = files.filter(file => {
            const isImage = file.type.startsWith('image/');
            if (!isImage) {
                console.warn('Datei ist kein Bild und wird ignoriert:', file.name, file.type);
            }
            return isImage;
        });

        if (imageFiles.length !== files.length) {
            alert(`${files.length - imageFiles.length} Datei(en) wurden ignoriert, da sie keine Bilder sind.`);
        }

        if (imageFiles.length > 0) {
            setUploadedImages(prev => [...prev, ...imageFiles]);
            console.log('Bilder hinzugefügt:', imageFiles.map(f => f.name));
        }

        event.target.value = '';
    };

    const removeUploadedImage = (index: number) => {
        setUploadedImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleCanvasMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
        setIsDrawing(true);
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                const rect = canvas.getBoundingClientRect();
                const x = event.clientX - rect.left;
                const y = event.clientY - rect.top;
                ctx.beginPath();
                ctx.moveTo(x, y);
            }
        }
    };

    const handleCanvasMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;

        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                const rect = canvas.getBoundingClientRect();
                const x = event.clientX - rect.left;
                const y = event.clientY - rect.top;
                ctx.lineTo(x, y);
                ctx.stroke();
            }
        }
    };

    const handleCanvasMouseUp = () => {
        setIsDrawing(false);
        const canvas = canvasRef.current;
        if (canvas) {
            setSignature(canvas.toDataURL());
        }
    };

    const getTouchPosition = (canvas: HTMLCanvasElement, touch: React.Touch) => {
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        
        return {
            x: (touch.clientX - rect.left) * scaleX,
            y: (touch.clientY - rect.top) * scaleY
        };
    };

    const handleCanvasTouchStart = (event: React.TouchEvent<HTMLCanvasElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDrawing(true);
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                const touch = event.touches[0];
                const pos = getTouchPosition(canvas, touch);
                ctx.beginPath();
                ctx.moveTo(pos.x, pos.y);
            }
        }
    };

    const handleCanvasTouchMove = (event: React.TouchEvent<HTMLCanvasElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isDrawing) return;

        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                const touch = event.touches[0];
                const pos = getTouchPosition(canvas, touch);
                ctx.lineTo(pos.x, pos.y);
                ctx.stroke();
            }
        }
    };

    const handleCanvasTouchEnd = (event: React.TouchEvent<HTMLCanvasElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDrawing(false);
        const canvas = canvasRef.current;
        if (canvas) {
            setSignature(canvas.toDataURL());
        }
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                setSignature(null);
            }
        }
    };

    const handleConfirm = () => {
        if (!rapportText.trim()) {
            return; // Rapport-Text ist erforderlich
        }

        onConfirm({
            rapportText: rapportText.trim(),
            selectedTaskImages,
            uploadedImages,
            signature: signatureRequired ? signature : null,
            anfahrt_abfahrt_mit_kfz: anfahrtAbfahrtMitKfz,
            material: material.trim(),
            arbeitszeit,
            anzahl_monteure: anzahlMonteure,
        });

        setRapportText('');
        setSelectedTaskImages([]);
        setUploadedImages([]);
        setSignature(null);
        setSignatureRequired(false);
        setAnfahrtAbfahrtMitKfz(false);
        setMaterial('');
        setArbeitszeit(0);
        setAnzahlMonteure(1);
        clearSignature();
    };

    const handleClose = () => {
        setRapportText('');
        setSelectedTaskImages([]);
        setUploadedImages([]);
        setSignature(null);
        setSignatureRequired(false);
        setAnfahrtAbfahrtMitKfz(false);
        setMaterial('');
        setArbeitszeit(0);
        setAnzahlMonteure(1);
        clearSignature();
        onClose();
    };

    const isValid = rapportText.trim().length > 0 && (!signatureRequired || signature !== null);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                className: 'modal-dialog'
            }}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <CheckIcon color="primary"/>
                    <Typography variant="h6">
                        Rapport für Aufgabe abschließen
                    </Typography>
                </Box>
                {taskTitle && (
                    <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                        {taskTitle}
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent className="modal-content" sx={{pb: 2}}>
                {/* Rapport Text */}
                <Card variant="outlined" sx={{mb: 3}}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            Rapport-Bericht *
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            value={rapportText}
                            onChange={(e) => setRapportText(e.target.value)}
                            placeholder="Beschreiben Sie die durchgeführten Arbeiten, besondere Vorkommnisse und Ergebnisse..."
                            variant="outlined"
                            required
                            error={!rapportText.trim() && rapportText.length > 0}
                            helperText={!rapportText.trim() && rapportText.length > 0 ? "Rapport-Text ist erforderlich" : `${rapportText.length} Zeichen`}
                        />

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={material}
                            onChange={(e) => setMaterial(e.target.value)}
                            placeholder="Verwendetes Material..."
                            variant="outlined"
                            label="Material"
                            sx={{ mt: 2 }}
                        />

                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Arbeitszeit (Stunden)"
                                    value={arbeitszeit}
                                    onChange={(e) => setArbeitszeit(Math.max(0, parseFloat(e.target.value) || 0))}
                                    variant="outlined"
                                    inputProps={{ min: 0, step: 0.5 }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label="Anzahl Monteure"
                                    value={anzahlMonteure}
                                    onChange={(e) => setAnzahlMonteure(Math.max(1, parseInt(e.target.value) || 1))}
                                    variant="outlined"
                                    inputProps={{ min: 1, step: 1 }}
                                />
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 2 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={anfahrtAbfahrtMitKfz}
                                        onChange={(e) => setAnfahrtAbfahrtMitKfz(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Anfahrt/Abfahrt mit KFZ"
                            />
                        </Box>
                    </CardContent>
                </Card>

                {/* Bilder */}
                <Card variant="outlined" sx={{mb: 3}}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <ImageIcon/>
                            Bilder anhängen (optional)
                        </Typography>

                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageUpload}
                            multiple
                            accept="image/*"
                            style={{display: 'none'}}
                        />

                        <Button
                            variant="outlined"
                            startIcon={<UploadIcon/>}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{mb: 2}}
                        >
                            Bilder hinzufügen
                        </Button>

                        {/* Existing Task Images */}
                        {taskImages && taskImages.length > 0 && (
                            <Box sx={{mb: 2}}>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                    Vorhandene Aufgaben-Bilder ({taskImages.length} verfügbar):
                                </Typography>

                                {/* "Alle auswählen" Checkbox */}
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedTaskImages.length === taskImages.length && taskImages.length > 0}
                                            indeterminate={selectedTaskImages.length > 0 && selectedTaskImages.length < taskImages.length}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedTaskImages([...taskImages]);
                                                } else {
                                                    setSelectedTaskImages([]);
                                                }
                                            }}
                                        />
                                    }
                                    label="Alle Bilder auswählen"
                                    sx={{mb: 2, fontWeight: 'bold'}}
                                />

                                <Grid container spacing={2}>
                                    {taskImages.map((image) => {
                                        const isSelected = selectedTaskImages.some(img => img.id === image.id);
                                        const backendUrl = import.meta.env.DEV
                                            ? 'http://localhost:8008'
                                            : import.meta.env.VITE_BACKEND_URL || '';
                                        const thumbnailUrl = image.image_path.startsWith('http')
                                            ? image.image_path
                                            : `${backendUrl}${image.image_path}`;

                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={image.id}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        p: 1.5,
                                                        border: '1px solid',
                                                        borderColor: isSelected ? 'primary.main' : 'grey.300',
                                                        borderRadius: 1,
                                                        backgroundColor: isSelected ? 'action.selected' : 'background.paper',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: isSelected ? 'action.selected' : 'action.hover',
                                                        }
                                                    }}
                                                    onClick={() => handleTaskImageToggle(image)}
                                                >
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onChange={() => handleTaskImageToggle(image)}
                                                        size="small"
                                                        sx={{mr: 1}}
                                                    />
                                                    <Box
                                                        component="img"
                                                        src={thumbnailUrl}
                                                        alt={`Bild ${image.id}`}
                                                        sx={{
                                                            width: 80,
                                                            height: 60,
                                                            objectFit: 'cover',
                                                            borderRadius: 1,
                                                            border: '1px solid',
                                                            borderColor: 'grey.300',
                                                            mr: 1.5,
                                                        }}
                                                        onError={(e) => {
                                                            console.error('Thumbnail failed to load:', thumbnailUrl);
                                                            const target = e.target as HTMLImageElement;
                                                            target.style.display = 'none';
                                                            const parent = target.parentElement;
                                                            if (parent && !parent.querySelector('.fallback-text')) {
                                                                const fallback = document.createElement('div');
                                                                fallback.className = 'fallback-text';
                                                                fallback.style.cssText = `
                                  width: 80px;
                                  height: 60px;
                                  border-radius: 4px;
                                  border: 1px solid #ddd;
                                  display: flex;
                                  align-items: center;
                                  justify-content: center;
                                  background: #f5f5f5;
                                  color: #999;
                                  font-size: 12px;
                                  margin-right: 12px;
                                `;
                                                                fallback.textContent = 'Bild nicht verfügbar';
                                                                parent.insertBefore(fallback, target.nextSibling);
                                                            }
                                                        }}
                                                        onLoad={() => {
                                                            console.log('Thumbnail loaded successfully:', thumbnailUrl);
                                                        }}
                                                    />
                                                    <Box sx={{flex: 1, minWidth: 0}}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                fontWeight: isSelected ? 'medium' : 'normal'
                                                            }}
                                                        >
                                                            {image.image_path.split('/').pop() || `Bild ${image.id}`}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            ID: {image.id}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Box>
                        )}

                        {/* Uploaded Images */}
                        {uploadedImages.length > 0 && (
                            <Box>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                    {uploadedImages.length} neue(s) Bild(er) hochgeladen:
                                </Typography>
                                <Grid container spacing={2}>
                                    {uploadedImages.map((file, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    p: 1.5,
                                                    border: '1px solid',
                                                    borderColor: 'grey.300',
                                                    borderRadius: 1,
                                                    backgroundColor: 'background.paper',
                                                    transition: 'all 0.2s',
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover',
                                                    }
                                                }}
                                            >
                                                <Box
                                                    component="img"
                                                    src={URL.createObjectURL(file)}
                                                    alt={file.name}
                                                    sx={{
                                                        width: 80,
                                                        height: 60,
                                                        objectFit: 'cover',
                                                        borderRadius: 1,
                                                        border: '1px solid',
                                                        borderColor: 'grey.300',
                                                        mr: 1.5,
                                                    }}
                                                />
                                                <Box sx={{flex: 1, minWidth: 0}}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        {file.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                                    </Typography>
                                                </Box>
                                                <IconButton
                                                    onClick={() => removeUploadedImage(index)}
                                                    size="small"
                                                    color="error"
                                                    sx={{ml: 1}}
                                                >
                                                    <DeleteIcon fontSize="small"/>
                                                </IconButton>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Unterschrifts-Option */}
                <Card variant="outlined">
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                            <SignatureIcon/>
                            Unterschrift
                        </Typography>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={signatureRequired}
                                    onChange={(e) => {
                                        setSignatureRequired(e.target.checked);
                                        if (!e.target.checked) {
                                            setSignature(null);
                                            clearSignature();
                                        }
                                    }}
                                />
                            }
                            label="Unterschrift vom Kunden erforderlich"
                        />

                        {signatureRequired && (
                            <Box sx={{border: '2px dashed #ccc', borderRadius: 2, p: 2, textAlign: 'center', mt: 2}}>
                                <canvas
                                    ref={canvasRef}
                                    width={400}
                                    height={150}
                                    style={{
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        cursor: 'crosshair',
                                        maxWidth: '100%',
                                        backgroundColor: 'white',
                                        touchAction: 'none'
                                    }}
                                    onMouseDown={handleCanvasMouseDown}
                                    onMouseMove={handleCanvasMouseMove}
                                    onMouseUp={handleCanvasMouseUp}
                                    onMouseLeave={handleCanvasMouseUp}
                                    onTouchStart={handleCanvasTouchStart}
                                    onTouchMove={handleCanvasTouchMove}
                                    onTouchEnd={handleCanvasTouchEnd}
                                    onTouchCancel={handleCanvasTouchEnd}
                                />
                                <Box sx={{mt: 1}}>
                                    <Button
                                        size="small"
                                        onClick={clearSignature}
                                        disabled={!signature}
                                    >
                                        Unterschrift löschen
                                    </Button>
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 1}}>
                                    Klicken und ziehen Sie mit der Maus oder verwenden Sie Ihren Finger auf Touch-Geräten, um zu unterschreiben
                                </Typography>
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {!isValid && (
                    <Alert
                        severity='error'
                        sx={{
                            mt: 2,
                            mb: 2,
                        }}>
                        {rapportText.trim().length === 0 && "Der Rapport-Bericht ist erforderlich, um die Aufgabe abzuschließen."}
                        {rapportText.trim().length > 0 && signatureRequired && !signature && "Eine Unterschrift ist erforderlich."}
                    </Alert>
                )}
            </DialogContent>

            <DialogActions className="modal-actions" sx={{px: 3, pb: 2, flexWrap: 'wrap'}}>
                <Button
                    onClick={handleClose}
                    className="modal-button modal-button-cancel"
                >
                    Abbrechen
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={!isValid}
                    className="modal-button modal-button-success"
                    variant="contained"
                >
                    <CheckIcon className="modal-icon"/>
                    Rapport speichern & Aufgabe beenden
                </Button>
            </DialogActions>
        </Dialog>
    );
}
